import { addValue } from '@dte/otw-utils-core/src/lists/addValue';
import { copyJson } from '@dte/otw-utils-core/src/objects/copyJson';
import { equalsIgnoreCase } from '@dte/otw-utils-core/src/strings/equalsIgnoreCase';
import { atom, useAtomValue } from 'jotai';
import { useSetAtomWithType } from '../../../../../../hooks/useSetAtomWithType';
import { ReportStep } from '../../ReportSteps';
import { reportSubmittedAtom, storedDraftReportAtom } from './draftReport';
import { draftReportAtom } from './draftReportAtoms';

const stepHistoryAtom = atom((get) => {
	const draftReport = get(draftReportAtom);
	const stepHistory = draftReport?.stepHistory || [];
	return stepHistory as ReportStep[];
});
stepHistoryAtom.debugLabel = 'stepHistory';

function updateStepHistory(stepHistory: string[], oldCurrentStep: string, newCurrentStep: string): string[] {
	const newCurrentStepIndex = stepHistory.indexOf(newCurrentStep);

	// We're going forward
	if (newCurrentStepIndex === -1) {
		return addValue(stepHistory, oldCurrentStep);
	}

	// We're going backwards
	return stepHistory.slice(0, newCurrentStepIndex);
}

const storedCurrentStepAtom = atom((get) => {
	const submitted = get(reportSubmittedAtom);
	if (submitted) {
		return ReportStep.Confirmation;
	}

	const storedDraftReport = get(storedDraftReportAtom);
	return storedDraftReport?.currentStep as ReportStep;
});
storedCurrentStepAtom.debugLabel = 'storedCurrentStep';

export const currentStepAtom = atom(
	(get) => {
		return get(storedCurrentStepAtom);
	},
	(get, set, value: string) => {
		// Do nothing once we've already submitted
		const submitted = get(reportSubmittedAtom);
		if (submitted) {
			return;
		}

		// Need to have an updated value
		if (!value) {
			return;
		}

		const draftReport = get(draftReportAtom);

		// Do nothing if we don't have a draft report
		if (!draftReport) {
			return;
		}

		const newDraftReport = copyJson(draftReport);

		const oldCurrentStep = get(storedCurrentStepAtom);
		const oldStepHistory = get(stepHistoryAtom);

		newDraftReport.currentStep = value;

		// Add the current step as the new last step, then remove everything after it
		// That covers both going back and going forward
		const newStepHistory = updateStepHistory(oldStepHistory, oldCurrentStep, value);
		newDraftReport.stepHistory = newStepHistory;

		set(draftReportAtom, newDraftReport);
	},
);
currentStepAtom.debugLabel = 'currentStep';

/**
 * Read from what is stored
 *
 * @returns
 */
export function useCurrentStep(): ReportStep {
	return useAtomValue(currentStepAtom);
}

/**
 * Write to the working copy
 *
 * @returns
 */
export function useSetCurrentStep(): (value: ReportStep | undefined) => void {
	return useSetAtomWithType(currentStepAtom);
}

export function useIsCurrentStep(stepName: ReportStep): boolean {
	const currentStep = useCurrentStep();
	return equalsIgnoreCase(currentStep, stepName);
}
